import { Injectable } from '@angular/core';
import {
    ConfigitAceAssignment,
    ConfigitAceConfiguration,
    ConfigitAceSection,
    ConfigitAceVariable,
} from '../../../models/configit-ace.model';
import { ConfigitAceService } from '../../../services/configit-ace.service';
import { map, switchMap } from 'rxjs/operators';
import { AppService } from '../../../services/app.service';
import { ConfigitAceQuestModelTransform } from './ace-quest-model-transform';
import { TranslateService } from '@ngx-translate/core';
import { Quest, QuestionType, QuestPartReset, QuestValueChange } from '@vi/quest';
import { Observable, of } from 'rxjs';
import {
    fromBooleanString,
    fromIsoDate,
    getAssignmentValue,
    isUserAssignment,
    singletonValues,
    userAssignments,
} from '../../../utils/configit-ace.helper';
import { uniqBy } from 'lodash';
import { Product } from '../../../services/products.service';
import { ConflictingValueDialogComponent } from '../configit-quest-adapter/conflicting-value-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConflictItem } from '../configit-quest-adapter/configit-types.interface';

@Injectable({
    providedIn: 'root',
})
export class AceQuestAdapterService {
    transformer: ConfigitAceQuestModelTransform;
    constructor(
        private appService: AppService,
        translateService: TranslateService,
        private api: ConfigitAceService,
        private dialog: MatDialog
    ) {
        this.transformer = new ConfigitAceQuestModelTransform(translateService);
    }

    public getConfiguration(
        id: string,
        existingAssignments: ConfigitAceAssignment[] = []
    ): Observable<ConfigitAceConfiguration> {
        const salesOrg = this.appService.getSalesOrg();
        const language = this.appService.getLanguage();
        // in configit ace, these variables always have to be set
        const assignments: ConfigitAceAssignment[] = uniqBy(
            [
                {
                    variableId: 'DIM_BUILDDATE',
                    instanceId: 'ROOT',
                    value: new Date().toISOString(),
                },
                {
                    variableId: 'SALESORG',
                    instanceId: 'ROOT',
                    value: salesOrg,
                },
                ...existingAssignments,
            ],
            'variableId'
        );
        return this.api.configure({ productId: id, salesOrg, language, assignments });
    }

    public getModel(id: string, existingAssignments: ConfigitAceAssignment[] = []) {
        return this.getConfiguration(id, existingAssignments).pipe(map((resp) => this.transformer.transform(resp)));
    }

    public check(changed: QuestValueChange): Observable<Quest> {
        const original: ConfigitAceConfiguration = changed.quest.original;
        const value = this.prepareValue(changed);
        const newAssignments = (Array.isArray(value) ? value : [value])
            .filter(Boolean)
            .map((value) => ({ variableId: changed.id, instanceId: changed.question?.instanceId, value }));
        const assignments = this.addAssignment(
            newAssignments,
            userAssignments(original.configuration.configurationAssignments)
        );
        return this.getConfiguration(original.product.id, assignments).pipe(
            switchMap((conf) => this.checkConflicts(conf, original)),
            map((resp) => this.transformer.transform(resp))
        );
    }

    public reset(reset: QuestPartReset) {
        const original: ConfigitAceConfiguration = reset.quest.original;
        const section = original.sections.find((s) => s.id === reset.part.id);
        const sectionVariables = section.variables.map((v) => v.id);
        const assignments = original.configuration.configurationAssignments.filter(
            (a) => isUserAssignment(a) && !sectionVariables.includes(a.variableId)
        );

        return this.getModel(original.product.id, assignments);
    }

    public getEnergyLabelFromConfiguration(model: Quest, productList: Product[]): Observable<string> {
        const config: ConfigitAceConfiguration = model.original;
        const assignments = config.configuration.configurationAssignments;
        const [relevant, solar, usageSolar] = ['ERP_LABEL_GENERATION', 'ERP_LABEL_SOLAR', 'ERP_LABEL_SOLAR_USAGE'].map(
            (s) => getAssignmentValue(s, assignments)
        );
        if (fromBooleanString(relevant)) {
            const solarUsage = fromBooleanString(solar) ? usageSolar : undefined;
            return this.api.getEnergyLabel({
                salesOrg: this.appService.getSalesOrg(),
                language: this.appService.getLanguage(),
                solarUsage,
                productList,
            });
        }
        return of('energylabelnotrelevant');
    }

    private prepareValue(changed: QuestValueChange) {
        if (changed.question.type === QuestionType.date && changed.value) {
            // configit requires date in local format
            return fromIsoDate(changed.value);
        }
        return changed.value;
    }

    private checkConflicts(
        updated: ConfigitAceConfiguration,
        old: ConfigitAceConfiguration
    ): Observable<ConfigitAceConfiguration> {
        const conflictingAssignments = updated.removedAssignments.variableAssignments.filter((assignment) => {
            const variable = updated.sections
                .map((sec) => this.findVariable(assignment.variable.id, sec))
                ?.find((result) => !!result);
            const value = singletonValues(variable?.values || []).find((v) => v.value === assignment.value.value);
            return value?.incompatible;
        });
        if (conflictingAssignments.length) {
            const data: ConflictItem[] = conflictingAssignments.map(({ variable, value }) => ({
                conflictingText: variable.name,
                conflictingValue: value.name,
                conflictingUnit: '',
            }));
            const prompt = this.dialog.open(ConflictingValueDialogComponent, { data, disableClose: true });
            return prompt.afterClosed().pipe(
                // and revert if not agreed to continue
                map((agree) => (agree ? updated : old))
            );
        }
        return of(updated);
    }

    private findVariable(id: string, section: ConfigitAceSection): ConfigitAceVariable | undefined {
        const found = section.variables.find((v) => v.id === id);
        return found ?? this.findVariableInSections(id, section.sections);
    }

    private findVariableInSections(id: string, sections: ConfigitAceSection[]): ConfigitAceVariable | undefined {
        return sections.map((sub) => this.findVariable(id, sub)).find((result) => !!result);
    }

    private addAssignment(
        newAssignments: ConfigitAceAssignment[],
        existingAssignments: ConfigitAceAssignment[]
    ): ConfigitAceAssignment[] {
        const assignmentsWithoutNew = existingAssignments.filter(
            (assignment) => !newAssignments.some((a) => a.variableId === assignment.variableId)
        );
        return [...assignmentsWithoutNew, ...newAssignments];
    }
}
